import React from 'react';
import * as Inovx from '@shared';
import EntitlementForm from './EntitlementForm';
import LinkedEntitlementForm from './LinkedEntitlementForm';
import EntitlementActions from '../actions/EntitlementActions';
import EntitlementStore from '../stores/EntitlementStore';
import EntitlementConstants from '../constants/EntitlementConstants';
import AuthenticationModeEnum from '../constants/AuthenticationModeEnum';

var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

declare var InovxLoader: any;

interface EntitlementProps {
}

interface EntitlementState {
    settings: IEntitlementSettings;
    allSecurityGroup?: ISecurityGroup[];
    modalVisible?: boolean;
    isLinkedGroup?: boolean;
    isActionInProgress?: boolean;
}

function getEntitlementState(): EntitlementState {
    return {
        settings: EntitlementStore.getSettings(),
        allSecurityGroup: EntitlementStore.getAll(),
        modalVisible: EntitlementStore.getModalVisible(),
        isLinkedGroup: EntitlementStore.isLinkedGroup(),
        isActionInProgress: SystemStore.isActionInProgress(EntitlementConstants.ENTITLEMENT_DELETE)
    };
}

function getSystemSecurityGroupDisplayName(name: string) {
    switch (name) {
        case 'System Administrator':
            return InovxLoader.t('Entitlement:systemGroups.systemAdministrator.name');
        case 'Project Administrator':
            return InovxLoader.t('Entitlement:systemGroups.projectAdministrator.name');
        case 'User':
            return InovxLoader.t('Entitlement:systemGroups.user.name');
        default:
            return name;
    }
}

function getSystemSecurityGroupDisplayDescription(name: string, description: string) {
    switch (name) {
        case 'System Administrator':
            return InovxLoader.t('Entitlement:systemGroups.systemAdministrator.description');
        case 'Project Administrator':
            return InovxLoader.t('Entitlement:systemGroups.projectAdministrator.description');
        case 'User':
            return InovxLoader.t('Entitlement:systemGroups.user.description');
        default:
            return description;
    }
}

class EntitlementApp extends Inovx.Shared.ReactExt.BaseAppComponent<EntitlementProps, EntitlementState> {
    constructor(props: EntitlementProps, context: any) {
        super('Entitlement', props, context);

        this.state = getEntitlementState();
    }

    private _onChange = () => {
        this.setState(getEntitlementState());
    }

    private _onSystemChange = () => {
        this.setState({
            isActionInProgress: SystemStore.isActionInProgress(EntitlementConstants.ENTITLEMENT_DELETE)
        });
    }

    private _hideModal = () => {
        EntitlementActions.finishEdit();
    }

    private _beginEditEntitlement = (e: Event, securityGroup: any) => {
        EntitlementActions.beginEdit(securityGroup);
    }

    private _deleteEntitlement = (e: Event, securityGroup: any) => {
        var messageObject = {
            message: InovxLoader.t('Entitlement:message.confirmDelete'),
            confirmCallback: function () {
                EntitlementActions.deleteEntitlement(securityGroup, null);
            }
        };
        SystemActions.displayConfirmationMessage(messageObject);
    }

    private _showAddEntitlement = () => {
        EntitlementActions.beginAdd(false);
    }

    private _showAddLinkedEntitlement = () => {
        EntitlementActions.beginAdd(true);
    }

    protected _initialize = () => {
        EntitlementActions.getAllEntitlement(() => {
            EntitlementActions.getSettings(() => {
                this._appLoaded = true;
            }, (error) => {
                this._handlePermissionError(error);
            });
        }, (error) => {
            this._handlePermissionError(error);
        }, true);
    }

    componentWillMount() {
        this.componentWillMountBase();
    }

    componentDidMount() {
        EntitlementStore.addChangeListener(this._onChange);
        SystemStore.addChangeListener(this._onSystemChange);
    }

    componentWillUnmount() {
        EntitlementStore.removeChangeListener(this._onChange);
        SystemStore.removeChangeListener(this._onSystemChange);
    }

    public render() {
        var columns = [{
            field: "Name",
            headerText: InovxLoader.t('Entitlement:grid.groupName'),
            type: "string",
            template: (securityGroup: ISecurityGroup) => <span>{securityGroup.IsSystemDefault ? getSystemSecurityGroupDisplayName(securityGroup.Name) : securityGroup.Name}</span>
        },{
            field: "Description",
            headerText: InovxLoader.t('Entitlement:grid.description'),
            type: "string",
            template: (securityGroup: ISecurityGroup) => <span>{securityGroup.IsSystemDefault ? getSystemSecurityGroupDisplayDescription(securityGroup.Name, securityGroup.Description) : (securityGroup.Description || '')}</span>
        },{
            field: "Type",
            headerText: InovxLoader.t('Entitlement:grid.type'),
            type: "string",
            width:"100px"
        }];

        var commands = {
            command: [
                {
                    iconClassName: 'glyphicon glyphicon-pencil',
                    className: 'grid-button',
                    click: this._beginEditEntitlement
                },{
                    iconClassName: 'glyphicon glyphicon-trash',
                    className: 'grid-button',
                    click: this._deleteEntitlement
                }],
            width: "85px",
            headerText: InovxLoader.t('Entitlement:grid.commands')
        };

        var defaultSort = [{
            field: "Name",
            dir: "ascending"
        }];

        var btns: any[] = [];
        btns.push({
            onClick: this._showAddEntitlement,
            btnUrl: `${UrlUtilities.getBaseApplicationUrl()}/images/addSecurityGroup.png`,
            btnAtt: {
                alt: InovxLoader.t('Entitlement:header.newSecurityGroup'),
                title: InovxLoader.t('Entitlement:header.newSecurityGroup'),
                id: 'entApp-addSecGroup-btn',
            }
        });

        if (this.state.settings.IsExternalAuthentication) {
            btns.push({
                onClick: this._showAddLinkedEntitlement,
                btnUrl: `${UrlUtilities.getBaseApplicationUrl()}/images/AddLinkedSecurityGroup.png`,
                btnAtt: {
                    alt: InovxLoader.t('Entitlement:header.newLinkedSecurityGroup'),
                    title: InovxLoader.t('Entitlement:header.newLinkedSecurityGroup'),
                    id: 'entApp-addLinkedSecGroup-btn',
                }
            });
        }

        var iconHeaderProps: any = {
            iconUrl: `${UrlUtilities.getBaseApplicationUrl()}/images/Entitlement.png`,
            iconAtt: { alt: InovxLoader.t('Entitlement:header.entitlement') },
            title: InovxLoader.t('Entitlement:header.entitlement'),
            rcClass: "cd-btn add-btn",
        };
        iconHeaderProps.btnUrls = btns;

        var allSecurityGroup = this.state.allSecurityGroup || [];
        return (
            <Inovx.Shared.Core.ReactLoader appReady={this.isAppLoaded() } errorStatus={this._errorStatus}>
                <div className="entitlement-app-content">
                    <Inovx.Shared.Layout.IconHeader {...iconHeaderProps} />
                    <Inovx.Shared.Controls.Grid dataSource={allSecurityGroup} commands={commands} columns={columns}
                        defaultSort={defaultSort} filter={true} sort={true} pageable={{ pageCount: 5, pageSize: 10, pageSizes: [10, 20, 50] }}/>
                </div>
                {!this.state.isLinkedGroup &&
                    <Inovx.Shared.Controls.ModalSlider isOpen={this.state.modalVisible} onClose={this._hideModal}>
                        <EntitlementForm />
                    </Inovx.Shared.Controls.ModalSlider>
                }

                {this.state.isLinkedGroup &&
                    <Inovx.Shared.Controls.ModalSlider isOpen={this.state.modalVisible} onClose={this._hideModal}>
                        <LinkedEntitlementForm />
                    </Inovx.Shared.Controls.ModalSlider>
                }
           </Inovx.Shared.Core.ReactLoader>
        );
    }
}

export default EntitlementApp;
